import { useDispatch } from 'react-redux';
import type { DocumentUpsertModel } from 'api/models';
import { StepFormLayout } from 'accounts/components/step-form-layout';
import { HOME_LICENSES_MI_STRINGS as STRINGS } from 'accounts/constants/strings/home-licenses-mi.strings';
import { useSetupFlowStep } from 'accounts/hooks/homeowners/use-setup-flow-step-context';
import { DateInputFinalFormAdapter } from 'components/DateInput/DateInput';
import Field from 'components/Field/Field';
import { InputFinalFormAdapter } from 'components/Input/Input';
import { maskMMDDYYYY, unmaskMMDDYYYY } from 'components/Input/masks';
import {
  MEDIA_LABEL,
  NewMediaUploadAdapter,
  validateMedia,
} from 'components/NewMediaUploader/NewMediaUploader';
import { Col, Row } from 'forkedlibraries/react-bootstrap';
import FormLayout from 'layouts/FormLayout/FormLayout';
import { IMAGE_FILE_TYPE } from 'models/enums/index';
import { uploadFiles } from 'store/redux/images/actions';
import { getString } from 'strings/index';
import {
  composeValidators,
  dateValidationMMDDYY,
  required,
  validPastOrTodayMMDDYYDate,
} from 'utils/validation';
import {
  getMICity,
  getMIProgram,
  isExpired,
  MI_PROGRAM,
  MILicensesFormValues,
  MIProgram,
  optionalDateMMDDYYYY,
  requiresCapture,
  requiresExpirationDate,
  showsIssuanceDate,
  toMILicensesFormValues,
  toMILicensesUpdatePayload,
} from './home-licenses-mi.utils';

/**
 * Copy for the three programs that capture something. They ask for the same shape — a number, an
 * expiry, a document — under different names, so the section below is written once and the wording is
 * looked up here instead of duplicating the markup per city.
 */
function getCaptureCopy(program: MIProgram | undefined, city?: string) {
  if (program === MI_PROGRAM.DETROIT) {
    return {
      title: STRINGS['detroit.title'],
      subtitle: STRINGS['detroit.subtitle'],
      numberLabel: STRINGS['detroit.number_label'],
      expirationLabel: STRINGS['detroit.expiration_label'],
      uploadLabel: STRINGS['detroit.upload_label'],
      info: STRINGS['detroit.info'],
    };
  }

  if (program === MI_PROGRAM.GRAND_RAPIDS) {
    return {
      title: STRINGS['grand_rapids.title'],
      subtitle: STRINGS['grand_rapids.subtitle'],
      numberLabel: STRINGS['grand_rapids.number_label'],
      expirationLabel: STRINGS['grand_rapids.expiration_label'],
      uploadLabel: STRINGS['grand_rapids.upload_label'],
      info: STRINGS['grand_rapids.info'],
    };
  }

  return {
    title: STRINGS['registration.title'],
    subtitle: city
      ? getString(STRINGS['registration.subtitle'], { city })
      : STRINGS['registration.subtitle.no_city'],
    numberLabel: STRINGS['registration.number_label'],
    expirationLabel: STRINGS['registration.expiration_label'],
    uploadLabel: STRINGS['registration.upload_label'],
    info: undefined,
  };
}

function InfoNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-lg rounded border border-solid border-green bg-green-translucent-light p-sm text-body text-navy">
      {children}
    </div>
  );
}

/**
 * Michigan variant of the Licenses step, rendered by the dispatcher when the home's market is MI.
 *
 * Which of the five variations shows is decided entirely by `mi.program`, resolved server-side from
 * the home's city. The client never picks it and never sends it back, so a home cannot talk its way
 * out of the step. Two of the five capture nothing and only confirm that the check was made.
 */
export function HomeLicensesMI() {
  const dispatch = useDispatch();
  const { currentStepFormData, goPreviousStep, handleSaveAndNext, setLoading } =
    useSetupFlowStep()!;

  const program = getMIProgram(currentStepFormData);
  const city = getMICity(currentStepFormData);
  const capturesData = requiresCapture(program);
  const copy = getCaptureCopy(program, city);

  // `uploadFiles` is a redux thunk; the loosely-typed `dispatch` widens its return to the thunk
  // itself, so cast to the resolved upload descriptors.
  async function uploadMedia(media: unknown) {
    const result = (await dispatch(
      uploadFiles(
        IMAGE_FILE_TYPE.LicenseRentalRegistrationMI,
        media as Record<string, unknown>,
      ),
    )) as unknown as DocumentUpsertModel[] | undefined;
    return result?.length ? result[0] : undefined;
  }

  async function handleSubmit(values: MILicensesFormValues) {
    let file: DocumentUpsertModel | undefined;

    if (capturesData && values.file) {
      setLoading?.(true);
      try {
        file = await uploadMedia(values.file);
      } catch (err) {
        console.error(err);
        setLoading?.(false);
        return;
      }
      setLoading?.(false);
    }

    await handleSaveAndNext?.(
      toMILicensesUpdatePayload(values, { program, file }),
    );
  }

  return (
    <StepFormLayout
      onSubmit={handleSubmit}
      handleBackStep={goPreviousStep}
      title={STRINGS.title}
      subTitle={
        city
          ? getString(STRINGS.subtitle, { city })
          : STRINGS['subtitle.no_city']
      }
      initialValues={toMILicensesFormValues(currentStepFormData)}
      getForm={({ values }: any) => (
        <>
          {capturesData && (
            <FormLayout.Section
              firstSection
              sectionTitle={copy.title}
              sectionSubTitle={copy.subtitle}
            >
              <Row>
                <Col xs={24} md={6}>
                  <Field
                    name="registrationNumber"
                    component={InputFinalFormAdapter}
                    placeholder={copy.numberLabel}
                    validate={required}
                  />
                </Col>
                {/* Detroit's Certificate of Compliance is the only one that records an issuance date. */}
                {showsIssuanceDate(program) && (
                  <Col xs={24} md={6}>
                    <Field
                      name="issuanceDate"
                      component={DateInputFinalFormAdapter}
                      mask={maskMMDDYYYY}
                      unmask={unmaskMMDDYYYY}
                      placeholder={STRINGS['detroit.issuance_label']}
                      validate={composeValidators(
                        required,
                        dateValidationMMDDYY,
                        validPastOrTodayMMDDYYDate,
                      )}
                    />
                  </Col>
                )}
                <Col xs={24} md={6}>
                  <Field
                    name="expirationDate"
                    component={DateInputFinalFormAdapter}
                    mask={maskMMDDYYYY}
                    unmask={unmaskMMDDYYYY}
                    placeholder={copy.expirationLabel}
                    // Only the format is enforced. A lapsed document is surfaced as the warning
                    // below rather than a field error, so the homeowner can continue while a
                    // renewal is in flight. `required` joins in only for the programs that issue an
                    // expiry — a plain city registration often has none to give.
                    validate={composeValidators(
                      ...(requiresExpirationDate(program) ? [required] : []),
                      optionalDateMMDDYYYY,
                    )}
                  />
                </Col>
              </Row>

              <div className="mt-lg">
                <Field
                  name="file"
                  validate={validateMedia}
                  component={NewMediaUploadAdapter}
                  mediaLabel={MEDIA_LABEL.FILE_UPLOAD}
                  customLabel={copy.uploadLabel}
                  disableWebcam
                  cleanOnMediaChange
                  isSingleMedia
                  ignoreError={false}
                  size="fluid"
                />
              </div>

              {isExpired(values?.expirationDate) && (
                <div className="mt-lg rounded border border-solid border-red bg-red-translucent-light p-sm text-body text-navy">
                  {STRINGS.expired_warning}
                </div>
              )}

              {copy.info && <InfoNote>{copy.info}</InfoNote>}

              {/* Ann Arbor's extra obligations are ours to satisfy, not fields to capture. */}
              {city === 'Ann Arbor' && (
                <InfoNote>{STRINGS['ann_arbor.extras']}</InfoNote>
              )}
            </FormLayout.Section>
          )}

          {/* A city program that exists but never reaches a Belong home — e.g. Troy registers only
              buildings with 3+ units. Confirmed rather than skipped, so it reads as checked. */}
          {program === MI_PROGRAM.NOT_REQUIRED_CONDITIONAL && (
            <FormLayout.Section
              firstSection
              sectionTitle={STRINGS['conditional.title']}
            >
              <InfoNote>
                {city
                  ? getString(STRINGS['conditional.info'], { city })
                  : STRINGS['conditional.info.no_city']}
              </InfoNote>
            </FormLayout.Section>
          )}

          {/* No city program at all. */}
          {program === MI_PROGRAM.NOT_REQUIRED && (
            <FormLayout.Section
              firstSection
              sectionTitle={STRINGS['none.title']}
            >
              <InfoNote>
                {city
                  ? getString(STRINGS['none.info'], { city })
                  : STRINGS['none.info.no_city']}
              </InfoNote>
            </FormLayout.Section>
          )}
        </>
      )}
    />
  );
}
